'use client';

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

export const WORKSPACE_MOBILE_MEDIA_QUERY = '(max-width: 63.999rem)';

export type WorkspaceViewportKind = 'pending' | 'mobile' | 'desktop';

function subscribeWorkspaceMobileViewport(onChange: () => void) {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return () => {};
  }

  const mediaQueryList = window.matchMedia(WORKSPACE_MOBILE_MEDIA_QUERY);
  mediaQueryList.addEventListener('change', onChange);
  return () => mediaQueryList.removeEventListener('change', onChange);
}

function readWorkspaceViewportKind(): WorkspaceViewportKind {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return 'desktop';
  }

  return window.matchMedia(WORKSPACE_MOBILE_MEDIA_QUERY).matches
    ? 'mobile'
    : 'desktop';
}

function readServerWorkspaceViewportKind(): WorkspaceViewportKind {
  return 'pending';
}

export function useWorkspaceViewportKind() {
  const viewportKind = useSyncExternalStore(
    subscribeWorkspaceMobileViewport,
    readWorkspaceViewportKind,
    readServerWorkspaceViewportKind
  );
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  return hasHydrated ? viewportKind : 'pending';
}

export function useWorkspaceIsMobileViewport() {
  return useWorkspaceViewportKind() === 'mobile';
}

export function useWorkspaceSidebarExpansion(defaultExpanded: boolean) {
  const viewportKind = useWorkspaceViewportKind();
  const [desktopExpanded, setDesktopExpanded] = useState(defaultExpanded);
  const setExpanded = useCallback(
    (expanded: boolean) => {
      if (viewportKind !== 'mobile') {
        setDesktopExpanded(expanded);
      }
    },
    [viewportKind]
  );

  return {
    expanded: viewportKind !== 'mobile' && desktopExpanded,
    setExpanded,
    viewportKind,
  };
}
